import { StandardEvents } from '@shopify/events';

/**
 * A progress bar toward the free shipping threshold.
 *
 * Liquid renders the initial state from the cart. This element keeps it current
 * when the cart changes without a page load.
 *
 * @extends {HTMLElement}
 */
class FreeShippingBar extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {AbortController | null} */
  #request = null;

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartUpdate, { signal });
    // Custom add-to-cart code on this store announces changes with this older event name.
    document.addEventListener('cart:update', this.#handleCartUpdate, { signal });

    // Liquid can't convert the threshold into a visitor's local currency, so it defers to us.
    if (this.hasAttribute('data-needs-render')) {
      this.#render(Number(this.dataset.total), this.dataset.currency);
    }
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#request?.abort();
  }

  /**
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleCartUpdate = async (event) => {
    try {
      await event.promise;
    } catch {
      // A failed cart change still leaves a cart worth re-reading.
    }

    this.#refresh();
  };

  async #refresh() {
    this.#request?.abort();
    this.#request = new AbortController();

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        signal: this.#request.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      this.#render(cart.total_price, cart.currency);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      throw error;
    }
  }

  /**
   * @param {number} total - Cart total in the cart currency's subunits.
   * @param {string | undefined} currency - ISO currency code of the cart.
   */
  #render(total, currency) {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const threshold = Number(this.dataset.threshold) * rate;
    if (!threshold || Number.isNaN(total)) return;

    const remaining = Math.max(threshold - total, 0);
    const progress = Math.min((total / threshold) * 100, 100);
    const unlocked = remaining === 0;

    this.toggleAttribute('data-unlocked', unlocked);
    this.removeAttribute('data-needs-render');
    this.style.setProperty('--free-shipping-progress', `${progress}%`);

    const message = this.querySelector('.free-shipping-bar__message');
    if (!message) return;

    if (unlocked) {
      message.textContent = this.dataset.messageUnlocked ?? '';
      return;
    }

    const [before = '', after = ''] = (this.dataset.messageRemaining ?? '').split('[amount]');
    const amount = document.createElement('strong');
    amount.textContent = this.#formatMoney(remaining, currency);
    message.replaceChildren(before, amount, after);
  }

  /**
   * @param {number} subunits
   * @param {string | undefined} currency
   */
  #formatMoney(subunits, currency) {
    const amount = subunits / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || this.dataset.currency,
      }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
